import { useState } from 'react';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Checkbox,
  Divider,
  Drawer,
  Group,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconBan, IconCheck, IconPlus, IconRotateClockwise, IconSearch } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { checkInn, checkKpp, checkOgrn, normalizeRequisite } from '@erp/shared';
import { ApiError } from '../api/client';
import type { OrganizationKind } from '../api/organizations';
import { COUNTERPARTY_KIND_LABELS, counterpartiesApi, type Counterparty, type CounterpartyInput } from '../api/counterparties';
import { BankAccountsEditor } from '../components/BankAccountsEditor';
import { ContactPersonsEditor } from '../components/ContactPersonsEditor';
import { useAuth } from '../auth/AuthContext';
import { RegistryPage } from '../components/RegistryPage';

const KIND_OPTIONS = Object.entries(COUNTERPARTY_KIND_LABELS).map(([value, label]) => ({ value, label }));

const emptyForm: CounterpartyInput = {
  shortName: '',
  fullName: '',
  kind: 'LEGAL_ENTITY',
  inn: '',
  kpp: '',
  ogrn: '',
  okpo: '',
  addressFull: '',
  actualAddressFull: '',
  phone: '',
  email: '',
  comment: '',
  edoParticipantId: '',
  edoInviteStatus: '',
};

/** Проверка реквизитов — те же функции, что и на сервере. ИНН обязателен
 * только для юрлица и ИП; физлицо и иностранный контрагент — без него. */
function validateRequisites(data: CounterpartyInput): string[] {
  const errors: string[] = [];

  if (!data.inn) {
    if (data.kind === 'LEGAL_ENTITY' || data.kind === 'ENTREPRENEUR') {
      errors.push('ИНН обязателен для юридического лица и ИП');
    }
  } else {
    const innCheck = checkInn(normalizeRequisite(data.inn));
    if (!innCheck.valid) errors.push(`ИНН: ${innCheck.error}`);
  }

  if (data.kind === 'LEGAL_ENTITY') {
    if (!data.kpp) errors.push('КПП обязателен для юридического лица');
    else {
      const kppCheck = checkKpp(normalizeRequisite(data.kpp));
      if (!kppCheck.valid) errors.push(`КПП: ${kppCheck.error}`);
    }
  }

  if (data.ogrn) {
    const ogrnCheck = checkOgrn(normalizeRequisite(data.ogrn));
    if (!ogrnCheck.valid) errors.push(`${data.kind === 'ENTREPRENEUR' ? 'ОГРНИП' : 'ОГРН'}: ${ogrnCheck.error}`);
  }

  return errors;
}

export function CounterpartiesPage() {
  const { hasRole } = useAuth();
  const canEdit = hasRole('ADMIN', 'ACCOUNTANT', 'MANAGER');
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [includeInactive, setIncludeInactive] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<CounterpartyInput>(emptyForm);
  const [formError, setFormError] = useState<string[] | null>(null);
  const [editingAccounts, setEditingAccounts] = useState<Counterparty['bankAccounts']>([]);
  const [editingPersons, setEditingPersons] = useState<Counterparty['contactPersons']>([]);

  const list = useQuery({
    queryKey: ['counterparties', { search: debouncedSearch, includeInactive }],
    queryFn: () => counterpartiesApi.list({ search: debouncedSearch, includeInactive }),
  });

  function openCreate() {
    setEditingId(null);
    setForm(emptyForm);
    setFormError(null);
    setEditingAccounts([]);
    setEditingPersons([]);
    setDrawerOpen(true);
  }

  function openEdit(cp: Counterparty) {
    setEditingId(cp.id);
    setForm({
      shortName: cp.shortName,
      fullName: cp.fullName,
      kind: cp.kind,
      inn: cp.inn ?? '',
      kpp: cp.kpp ?? '',
      ogrn: cp.ogrn ?? '',
      okpo: cp.okpo ?? '',
      addressFull: cp.addressFull ?? '',
      actualAddressFull: cp.actualAddressFull ?? '',
      phone: cp.phone ?? '',
      email: cp.email ?? '',
      comment: cp.comment ?? '',
      edoParticipantId: cp.edoParticipantId ?? '',
      edoInviteStatus: cp.edoInviteStatus ?? '',
    });
    setFormError(null);
    setEditingAccounts(cp.bankAccounts);
    setEditingPersons(cp.contactPersons);
    setDrawerOpen(true);
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload: CounterpartyInput = {
        ...form,
        inn: form.inn || null,
        kpp: form.kpp || null,
        ogrn: form.ogrn || null,
        okpo: form.okpo || null,
        addressFull: form.addressFull || null,
        actualAddressFull: form.actualAddressFull || null,
        phone: form.phone || null,
        email: form.email || null,
        comment: form.comment || null,
        edoParticipantId: form.edoParticipantId || null,
        edoInviteStatus: form.edoInviteStatus || null,
      };
      return editingId ? counterpartiesApi.update(editingId, payload) : counterpartiesApi.create(payload);
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ['counterparties'] });
      notifications.show({ color: 'green', message: 'Контрагент сохранён' });
      if (!editingId) {
        // Остаёмся в форме, переключившись в режим редактирования — чтобы можно
        // было сразу добавить банковский счёт или контактное лицо.
        setEditingId(data.counterparty.id);
        setEditingAccounts(data.counterparty.bankAccounts);
        setEditingPersons(data.counterparty.contactPersons);
      }
    },
  });

  function handleSubmit() {
    setFormError(null);
    const errors = validateRequisites(form);
    if (errors.length > 0) {
      setFormError(errors);
      return;
    }
    saveMutation.mutate(undefined, {
      onError: (err) => {
        setFormError(err instanceof ApiError ? (err.details ?? [err.message]) : ['Не удалось сохранить']);
      },
    });
  }

  const toggleActiveMutation = useMutation({
    mutationFn: (cp: Counterparty) =>
      cp.isActive ? counterpartiesApi.deactivate(cp.id) : counterpartiesApi.activate(cp.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['counterparties'] });
    },
  });

  const addBankAccount = useMutation({
    mutationFn: (data: Parameters<typeof counterpartiesApi.addBankAccount>[1]) =>
      counterpartiesApi.addBankAccount(editingId!, data),
    onSuccess: (data) => {
      setEditingAccounts((prev) => [...prev, data.bankAccount]);
      void queryClient.invalidateQueries({ queryKey: ['counterparties'] });
    },
  });

  const setDefaultBankAccount = useMutation({
    mutationFn: (accountId: string) =>
      counterpartiesApi.updateBankAccount(editingId!, accountId, { isDefault: true }),
    onSuccess: (data) => {
      setEditingAccounts((prev) =>
        prev.map((a) => (a.id === data.bankAccount.id ? data.bankAccount : { ...a, isDefault: false })),
      );
      void queryClient.invalidateQueries({ queryKey: ['counterparties'] });
    },
  });

  const deleteBankAccount = useMutation({
    mutationFn: (accountId: string) => counterpartiesApi.deleteBankAccount(editingId!, accountId),
    onSuccess: (_data, accountId) => {
      setEditingAccounts((prev) => prev.filter((a) => a.id !== accountId));
      void queryClient.invalidateQueries({ queryKey: ['counterparties'] });
    },
  });

  const addContactPerson = useMutation({
    mutationFn: (data: Parameters<typeof counterpartiesApi.addContactPerson>[1]) =>
      counterpartiesApi.addContactPerson(editingId!, data),
    onSuccess: (data) => {
      setEditingPersons((prev) => [...prev, data.contactPerson]);
      void queryClient.invalidateQueries({ queryKey: ['counterparties'] });
    },
  });

  const deleteContactPerson = useMutation({
    mutationFn: (personId: string) => counterpartiesApi.deleteContactPerson(editingId!, personId),
    onSuccess: (_data, personId) => {
      setEditingPersons((prev) => prev.filter((p) => p.id !== personId));
      void queryClient.invalidateQueries({ queryKey: ['counterparties'] });
    },
  });

  const items = list.data?.items ?? [];

  return (
    <RegistryPage
      title="Контрагенты"
      action={
        canEdit && (
          <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>
            Создать контрагента
          </Button>
        )
      }
      filters={
        <Group>
          <TextInput
            placeholder="Поиск по наименованию или ИНН"
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            w={320}
          />
          <Checkbox
            label="Показывать отключённые"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.currentTarget.checked)}
          />
        </Group>
      }
    >
      <Table striped highlightOnHover verticalSpacing="sm">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Наименование</Table.Th>
            <Table.Th>Вид</Table.Th>
            <Table.Th>ИНН</Table.Th>
            <Table.Th>КПП</Table.Th>
            <Table.Th>Статус</Table.Th>
            <Table.Th />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {items.map((cp) => (
            <Table.Tr key={cp.id} style={{ cursor: 'pointer' }} onClick={() => openEdit(cp)}>
              <Table.Td>
                <Text size="sm" fw={500}>
                  {cp.shortName}
                </Text>
              </Table.Td>
              <Table.Td>
                <Text size="sm" c="dimmed">
                  {COUNTERPARTY_KIND_LABELS[cp.kind]}
                </Text>
              </Table.Td>
              <Table.Td>{cp.inn ?? '—'}</Table.Td>
              <Table.Td>{cp.kpp ?? '—'}</Table.Td>
              <Table.Td>
                <Badge color={cp.isActive ? 'green' : 'gray'} variant="light">
                  {cp.isActive ? 'Активен' : 'Отключён'}
                </Badge>
              </Table.Td>
              <Table.Td onClick={(e) => e.stopPropagation()}>
                {canEdit && (
                  <Tooltip label={cp.isActive ? 'Отключить' : 'Включить'}>
                    <ActionIcon
                      variant="subtle"
                      color={cp.isActive ? 'red' : 'green'}
                      onClick={() => toggleActiveMutation.mutate(cp)}
                    >
                      {cp.isActive ? <IconBan size={16} /> : <IconRotateClockwise size={16} />}
                    </ActionIcon>
                  </Tooltip>
                )}
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>

      {!list.isLoading && items.length === 0 && (
        <Text c="dimmed" size="sm" ta="center" py="xl">
          Контрагенты не найдены
        </Text>
      )}

      <Drawer
        opened={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={editingId ? 'Контрагент' : 'Новый контрагент'}
        position="right"
        size="lg"
        padding="lg"
      >
        <Stack gap="sm">
          {formError && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
              <Stack gap={2}>
                {formError.map((e, i) => (
                  <Text key={i} size="sm">
                    {e}
                  </Text>
                ))}
              </Stack>
            </Alert>
          )}

          <Group grow>
            <TextInput
              label="Краткое наименование"
              value={form.shortName}
              onChange={(e) => setForm({ ...form, shortName: e.currentTarget.value })}
              required
              disabled={!canEdit}
            />
            <Select
              label="Вид"
              data={KIND_OPTIONS}
              value={form.kind}
              onChange={(v) => setForm((f) => ({ ...f, kind: (v as OrganizationKind) ?? f.kind }))}
              disabled={!canEdit}
              allowDeselect={false}
            />
          </Group>

          <TextInput
            label="Полное наименование"
            value={form.fullName}
            onChange={(e) => setForm({ ...form, fullName: e.currentTarget.value })}
            required
            disabled={!canEdit}
          />

          <Group grow>
            <TextInput
              label="ИНН"
              value={form.inn ?? ''}
              onChange={(e) => setForm({ ...form, inn: e.currentTarget.value })}
              required={form.kind === 'LEGAL_ENTITY' || form.kind === 'ENTREPRENEUR'}
              disabled={!canEdit}
            />
            {form.kind === 'LEGAL_ENTITY' && (
              <TextInput
                label="КПП"
                value={form.kpp ?? ''}
                onChange={(e) => setForm({ ...form, kpp: e.currentTarget.value })}
                required
                disabled={!canEdit}
              />
            )}
          </Group>

          <TextInput
            label={form.kind === 'ENTREPRENEUR' ? 'ОГРНИП' : 'ОГРН'}
            value={form.ogrn ?? ''}
            onChange={(e) => setForm({ ...form, ogrn: e.currentTarget.value })}
            disabled={!canEdit}
          />

          <TextInput
            label="Адрес"
            value={form.addressFull ?? ''}
            onChange={(e) => setForm({ ...form, addressFull: e.currentTarget.value })}
            disabled={!canEdit}
          />

          <TextInput
            label="Адрес доставки (грузополучатель), если отличается"
            value={form.actualAddressFull ?? ''}
            onChange={(e) => setForm({ ...form, actualAddressFull: e.currentTarget.value })}
            disabled={!canEdit}
          />

          <Group grow>
            <TextInput
              label="Телефон"
              value={form.phone ?? ''}
              onChange={(e) => setForm({ ...form, phone: e.currentTarget.value })}
              disabled={!canEdit}
            />
            <TextInput
              label="Email"
              value={form.email ?? ''}
              onChange={(e) => setForm({ ...form, email: e.currentTarget.value })}
              disabled={!canEdit}
            />
          </Group>

          <TextInput
            label="Комментарий"
            value={form.comment ?? ''}
            onChange={(e) => setForm({ ...form, comment: e.currentTarget.value })}
            disabled={!canEdit}
          />

          <TextInput
            label="Идентификатор участника ЭДО"
            value={form.edoParticipantId ?? ''}
            onChange={(e) => setForm({ ...form, edoParticipantId: e.currentTarget.value })}
            disabled={!canEdit}
          />

          {canEdit && (
            <Button
              leftSection={<IconCheck size={16} />}
              onClick={handleSubmit}
              loading={saveMutation.isPending}
              mt="xs"
            >
              Сохранить
            </Button>
          )}

          {editingId && (
            <>
              <Divider label="Банковские счета" mt="md" />
              <BankAccountsEditor
                accounts={editingAccounts}
                canEdit={canEdit}
                onAdd={(data) => addBankAccount.mutateAsync(data).then(() => undefined)}
                onSetDefault={(id) => setDefaultBankAccount.mutateAsync(id).then(() => undefined)}
                onDelete={(id) => deleteBankAccount.mutateAsync(id).then(() => undefined)}
              />

              <Divider label="Контактные лица" mt="md" />
              <ContactPersonsEditor
                persons={editingPersons}
                canEdit={canEdit}
                onAdd={(data) => addContactPerson.mutateAsync(data).then(() => undefined)}
                onDelete={(id) => deleteContactPerson.mutateAsync(id).then(() => undefined)}
              />
            </>
          )}
        </Stack>
      </Drawer>
    </RegistryPage>
  );
}
